import { TextInputProps } from './TextInput.types';
import { Grid, Stack, Tooltip } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { FocusEvent } from 'react';
import { FieldValues, Path, PathValue, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Icons } from 'greener-ui-library';

export function TextInput<C extends FieldValues>({
  control,
  name,
  type,
  additionalGridProps = {},
  gridSize = 6,
  fullWidth = true,
  variant = 'outlined',
  label,
  shrink = true,
  defaultValue = '',
  onBlurFn,
  readOnly = false,
  size = 'small',
  endAdornment,
  multiline,
  maxLength,
  helpLabel,
  ...props
}: TextInputProps<C>) {
  const {
    field: { ref, onBlur, ...inputProps },
    fieldState: { invalid, error },
  } = useController({
    name: name as Path<C>,
    control,
    defaultValue: defaultValue as PathValue<C, Path<C>>,
  });
  const { t } = useTranslation();
  function handleOnBlur(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) {
    if (onBlurFn) {
      onBlurFn(e);
    }
    onBlur();
  }

  return (
    <Grid item xs={gridSize} {...additionalGridProps}>
      <Stack direction="row" spacing={1} sx={{ placeItems: 'center' }}>
        <TextField
          inputProps={{ ...inputProps, maxLength }}
          onBlur={handleOnBlur}
          inputRef={ref}
          type={type}
          error={invalid}
          helperText={error && t(error.message as string)}
          variant={variant}
          fullWidth={fullWidth}
          label={label}
          InputProps={{
            ...(endAdornment && {
              endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment>,
            }),
            readOnly,
            multiline,
            rows: multiline ? 4 : undefined,
          }}
          InputLabelProps={{ shrink: shrink }}
          size={size}
          {...props}
        />
        {!!helpLabel && (
          <Tooltip placement="right" title={helpLabel} sx={{ cursor: 'pointer' }}>
            <Icons.HelpOutline />
          </Tooltip>
        )}
      </Stack>
    </Grid>
  );
}
